<template>
  <div :class="s.root">
    <div :class="s.body">
      <!-- 左: メッセージ一覧 -->
      <div :class="s.message_col">
        <div
          ref="messageList"
          :class="s.message_list">
          <!-- 追加ロード検知用番兵 -->
          <div ref="loadMoreSentinel" :class="s.sentinel" />
          <!-- 追加ロード中 -->
          <div v-if="flag.loadingMore" :class="s.loading_more">
            <i class="fa-solid fa-spinner fa-spin" />
          </div>
          <!-- 初期ロード中 -->
          <div v-if="flag.loading" :class="s.loading_center">
            <i class="fa-solid fa-spinner fa-spin fa-2x" />
          </div>
          <template v-else>
            <div v-if="!talks.length" :class="s.empty">メッセージがありません</div>
            <div
              v-for="talk in talks"
              :key="talk.id"
              :class="[s.message_wrap, isMine(talk) ? s.mine : s.theirs]">
              <div :class="[s.bubble, isMine(talk) ? s.bubble_mine : s.bubble_theirs]">
                {{ talk.message }}
              </div>
              <div :class="s.msg_time">
                <div>{{ formatTimestamp(talk.created_at, 'MM/DD HH:mm') }}</div>
                <div v-if="!isMine(talk)" class="ms-1">{{ talk.fromUser ? talk.fromUser.username : '不明' }}</div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 右: 送信エリア -->
      <div :class="s.send_col">
        <textarea
          v-model="inputMessage"
          :class="s.textarea"
          placeholder="メッセージを入力..."
          @keydown.ctrl.enter="sendTalk"
        />
        <div :class="s.send_hint">Ctrl + Enter で送信</div>
        <div :class="s.template_wrap" v-if="talkroomType === 'customer'">
          <select @change="selectedTemplate($event)"
            name="select-talk-template" id="select-talk-template">
            <option :value="''">--</option>
            <option :value="t.label" v-for="t in templates" :key="t.id">{{ t.label }}</option>
          </select>
          <button
            for="select-talk-template"
            :class="[s.send_btn, s.gray]"
            @click="focusTemplateSelect">
            <i v-if="flag.sending" class="fa-solid fa-spinner fa-spin me-1" />
            <i v-else class="fa-solid fa-list me-1" />
            定型文
          </button>
        </div>
        <button
          :class="[s.send_btn, s.gray]"
          @click="clearMessage">
          <i v-if="flag.sending" class="fa-solid fa-spinner fa-spin me-1" />
          <i v-else class="fa-solid fa-xmark me-1" />
          クリア
        </button>
        <button
          :class="s.send_btn"
          :disabled="!inputMessage.trim() || flag.sending"
          @click="sendTalk">
          <i v-if="flag.sending" class="fa-solid fa-spinner fa-spin me-1" />
          <i v-else class="fa-solid fa-paper-plane me-1" />
          送信
        </button>
      </div>
      <div
        v-if="flag.hasNewMessage"
        @click="getTalks()"
        :class="s.new_message" class="btn btn-outline-primary"><i class="fa-regular fa-circle-exclamation me-2"/>新着メッセージを確認</div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cf from '@/mixins/commonFunctions.js';

const LIMIT = 20;

export default {
  name: 'TalkroomMessages',
  props: ['data'],
  mixins: [cf],
  data() {
    return {
      flag: {
        initializing: false,
        loading: false,
        loadingMore: false,
        sending: false,
        hasNewMessage: false,
      },
      talkroom: null,
      talks: [],
      page: 1,
      totalPages: 1,
      inputMessage: '',
      observer: null,
    };
  },
  computed: {
    ...mapState(['user', 'helper']),
    talkroomId() {
      return this.talkroom?.id || null;
    },
    talkroomType() {
      const relations = this.talkroom?.relations || [];
      return relations.find((r) => r.foreign_type === 11 && r.user?.role?.role < 10) ? 'customer' : 'operator';
    },
    hasMore() {
      return this.page < this.totalPages;
    },
    officeIds() {
      return this.data?.officeIds || [];
    },
    templates() {
      return this.helper.messageTemplates;
    },
    updateCount() {
      return this.helper.updateCount.talkrooms;
    },
  },
  created() {
    this.initTalkroom();
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  watch: {
    updateCount() {
      this.flag.hasNewMessage = true;
    },
  },
  methods: {
    setupObserver() {
      if (this.observer) this.observer.disconnect();
      const sentinel = this.$refs.loadMoreSentinel;
      const container = this.$refs.messageList;
      if (!sentinel || !container) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.flag.loadingMore) {
            this.getTalks(true);
          }
        },
        { root: container, threshold: 0 },
      );
      this.observer.observe(sentinel);
    },

    isMine(talk) {
      return talk.from_user_id === this.user.id;
    },

    /** talkroom を取得 or 作成してからメッセージをロード */
    async initTalkroom() {
      // 既に talkroom がわかっている場合（一覧から遷移）はそのまま使う
      if (this.data?.talkroomId) {
        this.talkroom = this.data.talkroom || { id: this.data.talkroomId };
        this.getTalks();
        return;
      }

      const userIds = this.data?.userIds || [];
      if (!userIds.length || !this.officeIds.length) return;

      this.flag.initializing = true;
      try {
        // 既存 talkroom の取得を試みる（office集合 + user集合の完全一致）。
        // 開いただけではルームを作成しない（作成は初回送信時 = sendTalk 内）
        const detailRes = await this.axios.get('/v1/talkroom/get/detail', {
          params: { officeIds: this.officeIds, userIds, withRelations: 1 },
        });

        if (detailRes.data.status === 200 && detailRes.data.talkroom) {
          this.talkroom = detailRes.data.talkroom;
        }
      } catch (e) {
        alert('チャットの初期化に失敗しました。');
      } finally {
        this.flag.initializing = false;
        if (this.talkroom) this.getTalks();
      }
    },

    /** 初回送信時に talkroom を作成する（開いただけでは作らない） */
    async createTalkroom() {
      const userIds = this.data?.userIds || [];
      if (!userIds.length || !this.officeIds.length) return null;
      const createRes = await this.axios.post('/v1/talkroom/create', {
        office_ids: this.officeIds,
        user_ids: userIds,
      });
      if (createRes.data.status === 200) {
        this.talkroom = createRes.data.talkroom;
      }
      return this.talkroom;
    },

    async getTalks(loadMore = false) {
      if (!this.talkroomId) return;

      if (loadMore) {
        this.flag.loadingMore = true;
        this.page += 1;
      } else {
        this.flag.loading = true;
        this.page = 1;
        this.talks = [];
      }

      try {
        const res = await this.axios.get('/v1/talkroom/get/talk', {
          params: {
            talkroomId: this.talkroomId,
            page: this.page,
            limit: LIMIT,
            order: 'desc',
            withFromUser: 1,
          },
        });

        const paginator = res.data.talks || {};
        this.totalPages = paginator.meta?.last_page || 1;
        // descで来るので反転して古い順にする
        const incoming = (paginator.data || []).reverse();

        if (loadMore) {
          // 古いメッセージを先頭に追加しスクロール位置を維持
          // 送信済みメッセージによるページずれで重複する場合があるため dedup する
          const existingIds = new Set(this.talks.map((t) => t.id));
          const deduped = incoming.filter((t) => !existingIds.has(t.id));
          const container = this.$refs.messageList;
          const prevScrollHeight = container.scrollHeight;
          this.talks = [...deduped, ...this.talks];
          this.$nextTick(() => {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          });
        } else {
          this.talks = incoming;
          this.$nextTick(() => {
            this.scrollToBottom();
            this.setupObserver();
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!loadMore) this.flag.hasNewMessage = false;
        this.flag.loading = false;
        this.flag.loadingMore = false;
      }
    },

    scrollToBottom() {
      requestAnimationFrame(() => {
        const container = this.$refs.messageList;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    clearMessage() {
      this.inputMessage = '';
    },

    selectedTemplate(e) {
      const selectedMessage = e.target.value;
      if (e.target.value) this.inputMessage = this.inputMessage ? `${this.inputMessage}\n${selectedMessage}` : selectedMessage;
    },
    focusTemplateSelect() {
      const elem = document.getElementById('select-talk-template');
      if (elem) elem.click();
    },

    async sendTalk() {
      const text = this.inputMessage.trim();
      if (!text) return;
      this.flag.sending = true;
      try {
        // 初回送信時に talkroom を作成する
        if (!this.talkroomId) {
          const created = await this.createTalkroom();
          if (!created) return;
        }

        const res = await this.axios.post('/v1/talkroom/send/talk', {
          talkroom_id: this.talkroomId,
          from_user_id: this.user.id,
          message: text,
        });
        if (res.data.status === 200) {
          const newTalk = res.data.talk;
          newTalk.fromUser = { id: this.user.id, username: this.user.username };
          this.talks.push(newTalk);
          this.inputMessage = '';
          this.$nextTick(() => this.scrollToBottom());
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.flag.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" module="s">
.root {
  height: 60vh;
  display: flex;
  flex-direction: column;
}

.body {
  flex: 1;
  display: flex;
  gap: 0;
  min-height: 0;
  border: 1px solid var(--border-gray, #e0e0e0);
  border-radius: 6px;
  overflow: hidden;
}

/* ── 左: メッセージ列 ── */
.message_col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-gray, #e0e0e0);
}

.message_list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sentinel {
  height: 1px;
  flex-shrink: 0;
}

.loading_more {
  text-align: center;
  color: var(--label-secondary, #aaa);
  padding: 8px 0;
}

.loading_center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--label-secondary, #aaa);
}

.empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--label-secondary, #aaa);
}

/* ── メッセージ吹き出し ── */
.message_wrap {
  display: flex;
  flex-direction: column;
  max-width: 72%;
}

.theirs {
  align-self: flex-start;
  align-items: flex-start;
}

.mine {
  align-self: flex-end;
  align-items: flex-end;
}

.bubble {
  padding: 8px 12px;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
}

.bubble_theirs {
  background-color: var(--bg-gray, #f0f0f0);
  border-radius: 0 12px 12px 12px;
  color: #212529;
}

.bubble_mine {
  background-color: var(--bs-blue, #0d6efd);
  border-radius: 12px 0 12px 12px;
  color: #fff;
}

.msg_time {
  display: flex;
  align-items: center;
  color: var(--label-secondary, #aaa);
  margin-top: 3px;
  padding: 0 4px;
  > * {
    font-size: 14px;
  }
}

/* ── 右: 送信列 ── */
.send_col {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  background-color: var(--bg-gray, #f9f9f9);
}

.textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-gray, #e0e0e0);
  border-radius: 6px;
  padding: 10px 12px;
  outline: none;
  line-height: 1.5;
  background-color: #fff;

  &:focus {
    border-color: var(--bs-blue, #0d6efd);
  }
}

.send_hint {
  font-size: 11px;
  color: var(--label-secondary, #aaa);
  text-align: right;
}

.send_btn {
  width: 100%;
  padding: 10px;
  background-color: var(--bs-blue, #0d6efd);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;
  position: relative;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &.gray {
    background-color: var(--bs-gray);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
}
.template_wrap {
  position: relative;
  select {
    position: absolute;
    top: 0; left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
  }
  button {
    pointer-events: none;
  }
}
.new_message {
  position: absolute;
  bottom: 2em; left: 50%;
  translate: -50% 0;
  background-color: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}
</style>
